import { reaction } from 'mobx';
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { SchoolYearWithoutHolidayType } from '../components/SchoolYear/graphql/types';
import { useUserConfigContext } from './useUserConfigContext';

/**
 * Liest einen Wert aus dem UserConfigStore so, dass die Komponente auch ohne
 * `observer` neu rendert.
 *
 * Der Schuljahr-Wechsel wurde bisher an ueber hundert Stellen ueber
 * `useUserConfigContext().selectedSchoolYear` gelesen — in Komponenten, die teils
 * keine `observer` sind. Dass die UI trotzdem meist aktuell war, lag daran, dass
 * irgendwo anders ein Re-Render ausgeloest wurde. Diese Hooks machen die
 * Abhaengigkeit explizit.
 */
function useObservableValue<T>(compute: () => T): T {
  const computeRef = useRef(compute);
  computeRef.current = compute;

  const subscribe = useCallback((onStoreChange: () => void) => {
    return reaction(() => computeRef.current(), onStoreChange);
  }, []);

  return useSyncExternalStore(subscribe, () => computeRef.current());
}

/** Das aktuell gewaehlte Schuljahr; `null`, solange keines gesetzt ist. */
export function useSelectedSchoolYear(): SchoolYearWithoutHolidayType | null {
  const userConfig = useUserConfigContext();
  return useObservableValue(() => userConfig.selectedSchoolYear);
}

/**
 * Das Schuljahr vor dem gewaehlten — das letzte, das vor dessen Beginn endet;
 * `null`, wenn es keines gibt.
 */
export function usePreviousSchoolYear(): SchoolYearWithoutHolidayType | null {
  const userConfig = useUserConfigContext();
  return useObservableValue(() => userConfig.previousSchoolYear);
}
